import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';

import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as CoreActions from './core.actions';
import {
  initCore,
  loadMainVocabularies,
  savePreferences,
  setBreakpoint,
  setPreferences,
  switchLanguage,
} from './core.actions';
import { Action, select, Store } from '@ngrx/store';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { combineLatest, EMPTY, map, Observable, of } from 'rxjs';
import { getBrowserLang, TranslocoService } from '@ngneat/transloco';
import {
  ROUTE_DATA,
  ROUTE_IS_HOME,
  ROUTE_LANGUAGE,
  ROUTE_PAGE_ID,
  ROUTE_PROJECT_ID,
  ROUTED_ID,
  ROUTER_URL,
} from '@icilalune/router';
import {
  AppConfig,
  LanguageType,
  LunePreferences,
  RouteData,
} from '@icilalune/types';
import { Router } from '@angular/router';
import { LOCAL_STORAGE, LOCATION } from '@ng-web-apis/common';
import {
  PathService,
  setPathInfos,
} from '@madeinlune/ngrx-drupal-paths-to-routes';
import { SeoSocialShareData, SeoSocialShareService } from 'ngx-seo';
import { BREAKPOINT, PREFERENCES } from './core.providers';
import { isScullyGenerated, TransferStateService } from '@scullyio/ng-lib';
import { GeoLocalizationService } from '@icilalune/utils';
import { PwaService } from '@nx-icilalune/icilalune/pwa';
import {
  FacadeService,
  GetEntitiesPipe,
  GetEntityPipe,
  JsonApiQuery,
  selectRequestById,
  sendRequestError,
  setState,
} from '@nx-icilalune/data/access';
import {
  mainMenuQuery,
  projectDetailQuery,
  secondaryMenuQuery,
  termQueries,
} from '@nx-icilalune/data/icilalune/queries';
import {
  contextApps,
  contextMotionDesign,
  MainMenuItem,
  MainMenuTreeItem,
} from '@nx-icilalune/data/icilalune/types';
import {
  AppPathInfos,
  buildMenu,
  DrupalEntity,
  DrupalIdentifier,
  DrupalTerm,
} from '@nx-icilalune/drupal-types';
import {
  CURRENT_MAIN_MENU_ITEM,
  TERMS_BUSINESS_UNIT,
} from '@nx-icilalune/data/icilalune/tokens';
import { PROJECT_LIST_CONTEXT } from '@icilalune/list-feature/tokens';
import { isEqual } from '@madeinlune/ngx-operators';
import { TunnelService } from '@icilalune/three-tunnel/providers';
import { DialogService } from '@madeinlune/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { plainToInstance } from 'class-transformer';
import { DOCUMENT } from '@angular/common';
import { APP_CONFIG } from '@madeinlune/ngx-app-config';
import { Breakpoint } from '@icilalune/utils/responsive';
import { selectBreakpoint } from './core.selectors';
import { PwaManifestService } from '@madeinlune/ngx-pwa-manifest';
import {
  MAINTENANCE_ERROR,
  PATH_NOT_FOUND_ERROR,
} from '@icilalune/shell-state/tokens';

@Injectable()
export class CoreEffects implements OnInitEffects {
  loadCores$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CoreActions.initCore),
      switchMap(() => {
        if (isScullyGenerated()) {
          return this.transferStateService.getState('drupalDatas').pipe(
            take(1),
            map((drupalDatas: any) => {
              if (drupalDatas) {
                drupalDatas = { ...drupalDatas };
                if (drupalDatas.requests) {
                  const cleanedRequests = {};
                  Object.keys(drupalDatas.requests).forEach((lang) => {
                    const requests = drupalDatas.requests[lang];
                    Object.keys(requests).forEach((key) => {
                      if (requests[key]['complete']) {
                        cleanedRequests[key] = requests[key];
                      }
                    });
                  });
                  drupalDatas.requests = cleanedRequests;
                }

                this.store.dispatch(setState({ newState: drupalDatas }));
                if (drupalDatas?.entities) {
                  const data: DrupalIdentifier[] = Object.keys(
                    drupalDatas?.entities
                  )
                    .filter(
                      (key) =>
                        key.indexOf('node--') > -1 ||
                        key.indexOf('taxonomy_term--') > -1
                    )
                    .map((key) => {
                      return drupalDatas?.entities[key];
                    });
                  const pathInfos: AppPathInfos[] = data
                    .filter((r) => !!(r as any).path)
                    .map((resource) => {
                      return plainToInstance(AppPathInfos, resource, {
                        excludeExtraneousValues: true,
                        exposeUnsetFields: false,
                      });
                    });
                  if (pathInfos.length > 0) {
                    this.store.dispatch(setPathInfos({ pathInfos }));
                  }
                }
              }
            })
          );
        }
        return of(null);
      }),
      tap(() => {
        /***** REGISTER ICOMOON ICONS ******/
        this.matIconRegistry.addSvgIconSet(
          this.sanitizer.bypassSecurityTrustResourceUrl(
            './assets/icomoon/sprite.svg'
          )
        );
        const browserLang: string | undefined = getBrowserLang();
        const urlLanguage: string | undefined =
          this.location.pathname.split('/')?.[1];
        //
        const lang = this.translocoService.getActiveLang();
      }),
      delay(0),
      switchMap(() => {
        return this.translocoService.langChanges$.pipe(
          filter((lang) => !!lang),
          distinctUntilChanged(),
          switchMap((lang) => {
            return [
              CoreActions.loadPreferences(),
              CoreActions.loadMainMenu(),
              loadMainVocabularies(),
              CoreActions.loadSecondaryMenu(),
            ];
          })
        );
      })
    );
  });

  routedLanguageEffect$ = createEffect(
    () => {
      return this.routeLanguage$.pipe(
        filter((routeLanguage) => !!routeLanguage),
        tap((routeLanguage) => {
          if (routeLanguage) {
            this.document.documentElement.setAttribute('lang', routeLanguage);
          }
          this.translocoService.setActiveLang(routeLanguage);
        })
      );
    },
    { dispatch: false }
  );

  loadMainMenu$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.loadMainMenu),
        tap((action) => {
          this.dataAccessFacade.load(mainMenuQuery);
        })
      );
    },
    { dispatch: false }
  );

  loadSecondaryMenu$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(CoreActions.loadSecondaryMenu),
        tap((action) => {
          this.dataAccessFacade.load(secondaryMenuQuery);
        })
      );
    },
    { dispatch: false }
  );

  loadPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CoreActions.loadPreferences),
      switchMap((action) => {
        const rawPreferences: string | null =
          this.localStorage.getItem('preferences');
        let preferences: LunePreferences | null = null;
        if (rawPreferences) {
          preferences = JSON.parse(rawPreferences);
          if (preferences) {
            this.pwaService.installLater = preferences.pwaInstallLater;
          }
        }
        return [setPreferences({ preferences })];
      })
    );
  });

  savePreferences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CoreActions.savePreferences),
      switchMap((action) => {
        if (action.preferences) {
          this.localStorage.setItem(
            'preferences',
            JSON.stringify(action.preferences)
          );
        } else {
          this.localStorage.removeItem('preferences');
        }
        return [setPreferences({ preferences: action.preferences })];
      })
    );
  });

  setBreakPoints$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setBreakpoint),
        tap(({ breakpoint }) => {
          if (breakpoint) {
            this.tunnelService.breakpoint = breakpoint;
          }
        })
      );
    },
    { dispatch: false }
  );

  breakPoints$ = createEffect(() => {
    Breakpoints.XSmall = `(max-width: 420px)`;
    Breakpoints.Small = `(min-width: 420.1px) and (max-width: 768px)`;
    Breakpoints.Medium = `(min-width: 768.1px) and (max-width: 1024px)`;
    Breakpoints.Large = `(min-width: 1024.1px) and (max-width: 1920px)`;
    Breakpoints.XLarge = `(min-width: 1920.1px)`;
    const displayNameMap = new Map<string, Breakpoint>([
      [Breakpoints.XSmall, 'xs'],
      [Breakpoints.Small, 'sm'],
      [Breakpoints.Medium, 'md'],
      [Breakpoints.Large, 'lg'],
      [Breakpoints.XLarge, 'xl'],
    ]);
    return this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(
        switchMap((result) => {
          const actions: Action[] = [];
          for (const query of Object.keys(result.breakpoints)) {
            if (result.breakpoints[query]) {
              actions.push(
                setBreakpoint({ breakpoint: displayNameMap.get(query) })
              );
              break;
            }
          }
          return actions;
        })
      );
  });

  switchLanguage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(switchLanguage),
        tap((action) => {
          if (action.language === 'fr') {
            this.translocoService.setActiveLang('fr-FR');
          } else {
            this.translocoService.setActiveLang('en-US');
          }
        })
      );
    },
    { dispatch: false }
  );

  routedNodeEffect$ = createEffect(
    () => {
      return this.routeId$.pipe(
        filter((nodeId) => !!nodeId),
        switchMap((nodeId) => {
          return this.getEntityPipe.transform<DrupalEntity>(nodeId);
        }),
        isEqual(),
        withLatestFrom(
          this.routeIsHome$,
          this.translocoService.selectTranslate('seo.title-suffix'),
          this.routeData$,
          this.store.pipe(select(selectBreakpoint))
        ),
        tap(
          ([node, isHome, titleSuffix, routeData, breakpoint]: [
            DrupalEntity,
            boolean,
            string,
            RouteData,
            Breakpoint | undefined
          ]) => {
            if (node?.title) {
              const seoData: SeoSocialShareData = {
                title: `${node.title} | ${titleSuffix}`,
                description: node.summary,
                image: node.seoThumbnailUrl,
                url: `https://www.icilalune.com${node.path}`,
              };
              this.seoSocialShareService.setData(seoData);
            }
            if (node?.alternatives) {
              node.alternatives.forEach((alternative) => {
                this.seoSocialShareService.setLanguageAlternativeUrl(
                  alternative.id,
                  `https://www.icilalune.com${alternative.path}`
                );
              });
            }
            const isSettingsAndXs =
              routeData.context === 'detail:settings' && breakpoint === 'xs';
            if (!isHome && !isSettingsAndXs) {
              this.document.defaultView?.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }
          }
        )
      );
    },
    { dispatch: false }
  );

  geoLocalisationPrefsEffect$ = createEffect(() => {
    return this.geoLocalizationService.geoLocalizationState$.pipe(
      withLatestFrom(this.preferences$),
      switchMap(([localizationState, preferences]) => {
        const updatedPreferences: LunePreferences = { ...preferences } || {};
        updatedPreferences.localizeMe = localizationState;
        return [savePreferences({ preferences: updatedPreferences })];
      })
    );
  });

  pwaInstallLaterEffect$ = createEffect(() => {
    return this.pwaService.installLater$.pipe(
      withLatestFrom(this.preferences$),
      switchMap(([installLater, preferences]) => {
        const updatedPreferences: LunePreferences = { ...preferences } || {};
        updatedPreferences.pwaInstallLater = installLater;
        return [savePreferences({ preferences: updatedPreferences })];
      })
    );
  });

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(sendRequestError),
        debounceTime(500),
        tap(({ request, error }) => {
          const query: JsonApiQuery = request;
          if (error instanceof HttpErrorResponse && error?.status !== 503) {
            this.dialogService.openAlert({
              message: query.errorMessage ?? 'errors.generic.message',
              actions: [{ close: true, id: 'ok', label: 'OK', main: true }],
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  mainMenuLoaded$ = createEffect(() => {
    return this.store.pipe(
      select(selectRequestById(mainMenuQuery.queryId)),
      filter((query) => !query?.loading),
      switchMap((query) => {
        if (!query?.error) {
          return this.getEntitiesPipe
            .transform<MainMenuItem[]>(query?.allResultIds)
            .pipe(
              tap((mainMenuItems: MainMenuItem[] | undefined) => {
                if (mainMenuItems) {
                  const mainMenuTree = buildMenu<MainMenuTreeItem>(
                    mainMenuItems.filter((m) => !!m)
                  );
                  this.dataAccessFacade.setMenu(mainMenuTree, 'main');
                }
              })
            );
        }
        return of(query);
      }),
      isEqual(),
      switchMap((result) => {
        if (Array.isArray(result)) {
          return [CoreActions.appReady()];
        }
        return EMPTY;
      })
    );
  });

  secondaryMenuLoaded$ = createEffect(
    () => {
      return this.store.pipe(
        select(selectRequestById(secondaryMenuQuery.queryId)),
        filter((query) => !!query?.complete),
        switchMap((query) => {
          return this.getEntitiesPipe.transform<MainMenuItem[]>(
            query?.allResultIds
          );
        }),
        isEqual(),
        tap((mainMenuItems: MainMenuItem[] | undefined) => {
          if (mainMenuItems) {
            const mainMenuTree = buildMenu<MainMenuTreeItem>(
              mainMenuItems.filter((m) => !!m)
            );
            this.dataAccessFacade.setMenu(mainMenuTree, 'secondary');
          }
        })
      );
    },
    { dispatch: false }
  );

  detailViewNavigated$ = createEffect(() => {
    return combineLatest([this.routeProjectId$]).pipe(
      switchMap(([projectId]) => {
        const actions: Action[] = [];
        let detailQuery: JsonApiQuery | undefined;
        if (projectId) {
          detailQuery = { ...projectDetailQuery, id: projectId };
          actions.push(loadMainVocabularies());
        }
        if (detailQuery) {
          this.dataAccessFacade.load(detailQuery);
        }
        return actions;
      })
    );
  });

  loadMainVocabularies$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(loadMainVocabularies),
        tap(() => {
          this.dataAccessFacade.loadCollection(termQueries);
        })
      );
    },
    { dispatch: false }
  );

  loadPage$ = createEffect(
    () => {
      return this.routePageId$.pipe(
        filter((pageId) => !!pageId),
        withLatestFrom(this.routeData$),
        tap(([routePageId, routeData]) => {
          const mainQuery = routeData?.queries?.['main'];
          if (mainQuery) {
            this.dataAccessFacade.load({ ...mainQuery, id: routePageId });
          }
        })
      );
    },
    { dispatch: false }
  );

  loadDetailEntity$ = createEffect(
    () => {
      return this.routeId$.pipe(
        filter((routeId) => !!routeId),
        withLatestFrom(this.routeData$),
        filter(([routeId, routeData]) => {
          return (
            routeData.context === 'detail:news' ||
            routeData.context === 'detail:blog' ||
            routeData.context === 'detail:solution' ||
            routeData.context === 'detail:client'
          );
        }),
        tap(([id, routeData]) => {
          const queries: JsonApiQuery[] = [];
          const mainQuery = routeData?.queries?.['main'];
          if (mainQuery) {
            queries.push({ ...mainQuery, id });
          }
          this.dataAccessFacade.loadCollection(queries);
        })
      );
    },
    { dispatch: false }
  );

  setTunnelDetailContext$ = createEffect(
    () => {
      return this.routeId$.pipe(
        filter((routeId) => !!routeId),
        withLatestFrom(this.routeData$, this.breakpoint$),
        tap(([id, routeData, breakpoint]) => {
          if (routeData.context === 'detail:odyssee') {
            this.tunnelService.index = 8;
            this.tunnelService.context = 'odyssee';
          } else if (routeData.context === 'detail:settings') {
            this.tunnelService.index = 1;
            this.tunnelService.context = 'detail';
          } else if (routeData.context === 'list:blog') {
            this.tunnelService.index = 7;
            this.tunnelService.context = 'list';
          } else if (routeData.context === 'list:news') {
            this.tunnelService.index = 6;
            this.tunnelService.context = 'list';
          } else if (routeData.context === 'list:clients') {
            this.tunnelService.index = 5;
            this.tunnelService.context = 'list';
          } else if (routeData.context === 'list:projects') {
            this.tunnelService.index = 2;
            this.tunnelService.context = 'list';
          } else if (routeData.context === 'detail:solution') {
            this.tunnelService.index = 4;
            this.tunnelService.context = 'detail';
          } else if (routeData.context === 'detail:project') {
            this.tunnelService.index = 2;
            this.tunnelService.context = 'detail';
          } else if (routeData.context === 'detail:services') {
            this.tunnelService.index = 2;
            this.tunnelService.context = 'detail';
          } else if (routeData.context === 'detail:client') {
            this.tunnelService.index = 5;
            this.tunnelService.context =
              breakpoint === 'xs' ? 'detail' : 'list';
          } else if (routeData.context === 'detail:madeinlune') {
            this.tunnelService.index = 2;
            this.tunnelService.context = 'detail';
          } else if (routeData.context === 'detail:blog') {
            this.tunnelService.index = 7;
            this.tunnelService.context = 'detail';
          } else if (routeData.context === 'detail:news') {
            this.tunnelService.index = 6;
            this.tunnelService.context = 'detail';
          }
        })
      );
    },
    { dispatch: false }
  );

  setTunnelListContext$ = createEffect(
    () => {
      return this.routeId$.pipe(
        filter((routeId) => !!routeId),
        withLatestFrom(this.routeData$),
        filter(([routeId, routeData]) => {
          return (
            (routeData.context && routeData.context.indexOf('list') > -1) ||
            false
          );
        }),
        tap(([id, routeData]) => {
          this.tunnelService.context = 'list';
        })
      );
    },
    { dispatch: false }
  );

  pathNotFoundErrorEffect$ = createEffect(
    () => {
      return this.pathNotFoundError$.pipe(
        filter((pathNotFoundError) => !!pathNotFoundError),
        tap((pathNotFoundError) => {
          this.dialogService.openAlert({
            message: 'errors.not-found.message',
            actions: [{ close: true, id: 'ok', label: 'OK', main: true }],
          });
          const lang = this.transloco.getActiveLang().split('-').shift();
          this.router.navigateByUrl(`/${lang}/`);
        })
      );
    },
    { dispatch: false }
  );

  maintenanceErrorEffect$ = createEffect(
    () => {
      return this.maintenanceError$.pipe(
        filter((maintenanceError) => !!maintenanceError),
        tap((maintenanceError) => {
          this.dialogService.openAlert({
            message: 'errors.maintenance.message',
            actions: [{ close: true, id: 'ok', label: 'OK', main: true }],
          });
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    @Inject(DOCUMENT)
    private document: Document,
    @Inject(ROUTE_LANGUAGE) private routeLanguage$: Observable<LanguageType>,
    @Inject(LOCATION) private location: Location,
    @Inject(LOCAL_STORAGE) private localStorage: Storage,
    @Inject(PREFERENCES) private preferences$: Observable<LunePreferences>,
    @Inject(ROUTE_PROJECT_ID) private routeProjectId$: Observable<string>,
    @Inject(ROUTE_PAGE_ID) private routePageId$: Observable<string>,
    @Inject(ROUTER_URL) private routeUrl$: Observable<string>,
    @Inject(ROUTE_DATA) private routeData$: Observable<RouteData>,
    @Inject(ROUTED_ID) private routeId$: Observable<string | undefined>,
    @Inject(ROUTE_IS_HOME) private routeIsHome$: Observable<boolean>,
    @Inject(PROJECT_LIST_CONTEXT)
    private projectListContext$: Observable<
      typeof contextApps | typeof contextMotionDesign
    >,
    @Inject(CURRENT_MAIN_MENU_ITEM)
    private currentMainMenuItem$: Observable<MainMenuItem | undefined>,
    @Inject(TERMS_BUSINESS_UNIT)
    private businessUnitTerms$: Observable<DrupalTerm[]>,
    @Inject(BREAKPOINT)
    private breakpoint$: Observable<Breakpoint>,
    private matIconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private breakpointObserver: BreakpointObserver,
    private translocoService: TranslocoService,
    private pathService: PathService,
    private router: Router,
    private store: Store<any>,
    private readonly seoSocialShareService: SeoSocialShareService,
    private transferStateService: TransferStateService,
    private geoLocalizationService: GeoLocalizationService,
    private pwaService: PwaService,
    private getEntitiesPipe: GetEntitiesPipe,
    private getEntityPipe: GetEntityPipe,
    private dataAccessFacade: FacadeService,
    public tunnelService: TunnelService,
    private dialogService: DialogService,
    private applicationRef: ApplicationRef,
    private pwaManifestService: PwaManifestService,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    @Inject(PATH_NOT_FOUND_ERROR) private pathNotFoundError$: Observable<any>,
    @Inject(MAINTENANCE_ERROR) private maintenanceError$: Observable<any>,
    private transloco: TranslocoService
  ) {}

  ngrxOnInitEffects(): Action {
    return initCore();
  }

  getDataAsArray<T>(data: { [id: string]: T }): T[] {
    return Object.keys(data).map((id) => {
      return data[id];
    });
  }
}
